import { useEffect, useRef } from "react";
import { Navigate, Outlet } from "react-router-dom";
import { useAdminAuthStore } from "@/stores/admin-auth-store";

export function ProtectedRoute() {
  const { isAuthenticated, isLoading, initAuth } = useAdminAuthStore();
  const initialized = useRef(false);

  useEffect(() => {
    if (!initialized.current) {
      initialized.current = true;
      initAuth();
    }
  }, [initAuth]);

  // Defeat the browser's back-forward cache (bfcache) after logout.
  // On macOS especially, a Back / two-finger swipe restores the DOM AND the
  // frozen JS state (including zustand's `isAuthenticated: true`) instantly,
  // showing the authenticated Dashboard for a beat before any React effect
  // can redirect. When the page is served from bfcache and the token has been
  // cleared (i.e. user logged out in the meantime), force a hard reload so
  // React re-runs from scratch and the auth guard redirects to /login
  // BEFORE any protected content paints.
  useEffect(() => {
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted) return;
      const hasToken = !!localStorage.getItem("admin_access_token");
      if (!hasToken) {
        window.location.reload();
      } else {
        initAuth();
      }
    };
    window.addEventListener("pageshow", handlePageShow);
    return () => window.removeEventListener("pageshow", handlePageShow);
  }, [initAuth]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-[#646464]">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <Outlet />;
}
